/**
 * Settings Store for Auxilio Travel
 * Manages user preferences like distance units
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type DistanceUnit = 'km' | 'miles' | 'auto';
export type TemperatureUnit = 'celsius' | 'fahrenheit' | 'auto';

// Countries that primarily use miles
const MILES_LOCALES = ['en-US', 'en-GB', 'en-MM', 'en-LR'];

// Countries that primarily use Fahrenheit
const FAHRENHEIT_LOCALES = ['en-US'];

function detectDistanceUnit(): 'km' | 'miles' {
  const locale = navigator.language || 'en-US';
  // US, UK, Myanmar, Liberia use miles
  if (MILES_LOCALES.some(ml => locale.startsWith(ml.split('-')[0]) && locale === ml)) {
    return 'miles';
  }
  // Broader check: en-US is most common miles locale
  if (locale === 'en-US') return 'miles';
  return 'km';
}

function detectTemperatureUnit(): 'celsius' | 'fahrenheit' {
  const locale = navigator.language || 'en-US';
  if (FAHRENHEIT_LOCALES.some(fl => locale === fl)) {
    return 'fahrenheit';
  }
  return 'celsius';
}

interface SettingsState {
  distanceUnit: DistanceUnit;
  setDistanceUnit: (unit: DistanceUnit) => void;
  /** Returns the effective unit ('km' or 'miles'), resolving 'auto' to locale */
  getEffectiveUnit: () => 'km' | 'miles';
  temperatureUnit: TemperatureUnit;
  setTemperatureUnit: (unit: TemperatureUnit) => void;
  /** Returns the effective temp unit ('celsius' or 'fahrenheit'), resolving 'auto' to locale */
  getEffectiveTemperatureUnit: () => 'celsius' | 'fahrenheit';
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      distanceUnit: 'auto',
      setDistanceUnit: (unit) => set({ distanceUnit: unit }),
      getEffectiveUnit: () => {
        const unit = get().distanceUnit;
        if (unit === 'auto') return detectDistanceUnit();
        return unit;
      },
      temperatureUnit: 'auto',
      setTemperatureUnit: (unit) => set({ temperatureUnit: unit }),
      getEffectiveTemperatureUnit: () => {
        const unit = get().temperatureUnit;
        if (unit === 'auto') return detectTemperatureUnit();
        return unit;
      },
    }),
    {
      name: 'auxilio-travel-settings',
    }
  )
);
